import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './ProductDetails.css';
import { useAuth } from '../../contexts/AuthContext';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import { addCartItem } from '../../firebase/Cart';
import { uploadDesignImagesToFirebase } from '../../utils/firebaseStorage';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import {
    getTShirtProducts,
    getProductsBySubcategory,
    getStockForSize,
    getTotalStockForColor,
} from '../../firebase/products';
import AuthModal from '../AuthModal/AuthModal';
import UploadImageModal from '../UploadImageModal/UploadImageModal';
import ProductDescriptionBlock from '../ProductDescription/ProductDescriptionBlock';
import DynamicProductDetailsView from './DynamicProductDetailsView';
import placeholderShirt from '../../assets/images/placeholder-shirt.png';

const LOW_STOCK_THRESHOLD = 10;

// ── Subcategory detection ─────────────────────────────────────────────────────
const SUBCATEGORY_TSHIRT  = 'T-Shirt';
const SUBCATEGORY_JERSEY  = 'Jersey';
const SUBCATEGORY_POLO    = 'Polo Shirt';

// ── Imprint prices per shape ──────────────────────────────────────────────────
const IMPRINT_PRICES_RECTANGLE = {
    'Full Front':150,'Medium Front':120,'Center Chest':50,'Across Chest':80,
    'Left Chest':50,'Right Chest':50,'Left Vertical':120,'Right Vertical':120,
    'Bottom Left':50,'Bottom Right':50,'Full Back':150,'Medium Back':120,
    'Locker Center Patch':50,'Across Shoulder':80,'Left Sleeve':40,'Right Sleeve':40,
};
const IMPRINT_PRICES_CIRCLE = {
    'Full Front':120,'Medium Front':80,'Center Chest':50,'Across Chest':80,
    'Left Chest':40,'Right Chest':40,'Left Vertical':100,'Right Vertical':100,
    'Bottom Left':50,'Bottom Right':50,'Full Back':120,'Medium Back':80,
    'Locker Center Patch':50,'Back Center':80,'Left Sleeve':40,'Right Sleeve':40,
};
const getImprintPrice  = (loc, shape) => (shape==='rectangle'?IMPRINT_PRICES_RECTANGLE:IMPRINT_PRICES_CIRCLE)[loc]||0;
const calcImprintTotal = (front,back,sleeves,shape) => {
    const all=[...front,...back,...(sleeves.includes('left')?['Left Sleeve']:[]),...(sleeves.includes('right')?['Right Sleeve']:[])];
    return all.length===0?0:all.reduce((s,l)=>s+getImprintPrice(l,shape),0);
};

// ── Color maps ────────────────────────────────────────────────────────────────
const COLOR_NAME_MAP = {
    'Black':'black','Red':'red','Yellow':'yellow','Orange':'orange',
    'White':'white','Blue':'blue','Green':'green','Navy':'navy',
    'Gray':'gray','Grey':'gray','Pink':'pink','Purple':'purple','Brown':'brown',
};
const COLOR_HEX_MAP = {
    'black':'#1f1f1f','red':'#EF4444','yellow':'#FCD34D','orange':'#F97316',
    'white':'#FFFFFF','blue':'#1a5faa','green':'#2e7d32','navy':'#1a237e',
    'gray':'#888888','pink':'#e91e8c','purple':'#7b1fa2','brown':'#5d4037',
};

// ── Size maps ─────────────────────────────────────────────────────────────────
const ALL_SIZES=['xxs','xs','small','medium','large','xl','xxl','xxxl','xxxxl','xxxxxl'];
const SIZE_LABELS={xxs:'XXS',xs:'XS',small:'Small',medium:'Medium',large:'Large',xl:'XL',xxl:'2XL',xxxl:'3XL',xxxxl:'4XL',xxxxxl:'5XL'};
const SIZE_KEY_TO_DISPLAY = {
    xxs:'XXS',xs:'XS',small:'Small',medium:'Medium',large:'Large',
    xl:'XL',xxl:'2XL',xxxl:'3XL',xxxxl:'4XL',xxxxxl:'5XL',
};

// ── Print location options ────────────────────────────────────────────────────
const FRONT_OPTIONS=['Full Front','Medium Front','Center Chest','Across Chest','Left Chest','Right Chest','Left Vertical','Right Vertical','Bottom Left','Bottom Right'];
const BACK_OPTIONS_CIRCLE=['Full Back','Medium Back','Locker Center Patch','Back Center'];
const BACK_OPTIONS_RECTANGLE=['Full Back','Medium Back','Locker Center Patch','Across Shoulder'];
const BACK_CONFLICT_RULES = {
    'Back Center': ['Locker Center Patch'],
    'Locker Center Patch': ['Back Center'],
};

// ── Front overlap rules ───────────────────────────────────────────────────────
const FRONT_OVERLAP_RULES={
    'Full Front':['Medium Front','Center Chest','Across Chest','Left Chest','Right Chest','Left Vertical','Right Vertical','Bottom Left','Bottom Right'],
    'Medium Front':['Full Front','Center Chest','Left Chest','Right Chest'],
    'Across Chest':['Full Front','Center Chest','Left Chest','Right Chest'],
    'Left Vertical':['Full Front','Left Chest','Bottom Left'],
    'Right Vertical':['Full Front','Right Chest','Bottom Right'],
    'Center Chest':['Full Front','Medium Front','Across Chest'],
    'Left Chest':['Full Front','Medium Front','Across Chest','Left Vertical'],
    'Right Chest':['Full Front','Medium Front','Across Chest','Right Vertical'],
    'Bottom Left':['Full Front','Left Vertical'],
    'Bottom Right':['Full Front','Right Vertical'],
};

const getBlockedFrontOptions=(selected)=>{const b=new Set();selected.forEach(o=>(FRONT_OVERLAP_RULES[o]||[]).forEach(x=>b.add(x)));return b;};
const getBlockedBackOptions = (selected) => {
  const blocked = new Set();
  selected.forEach(o => (BACK_CONFLICT_RULES[o] || []).forEach(x => blocked.add(x)));

  // Full Back stays exclusive with every other back location.
  if (selected.includes('Full Back')) {
    [...BACK_OPTIONS_CIRCLE, ...BACK_OPTIONS_RECTANGLE]
      .forEach(opt => { if (opt !== 'Full Back') blocked.add(opt); });
  } else if (selected.length > 0) {
    blocked.add('Full Back');
  }
  return blocked;
};

const MAX_FRONT_LOCATIONS = 3;
const MAX_BACK_LOCATIONS = 2;
const SIDE_LABELS={front:'Front',back:'Back',left:'Left Sleeve',right:'Right Sleeve'};

// ── Stock helpers (T-Shirt / color-keyed) ─────────────────────────────────────
function getSizeStock(inventory,colorName,sizeKey){
    if(!inventory)return null;
    const fsName=Object.keys(COLOR_NAME_MAP).find(k=>COLOR_NAME_MAP[k]===colorName)||colorName;
    const cs=inventory[fsName]||inventory[colorName]||null;
    if(!cs)return null;
    return getStockForSize(cs,sizeKey);
}
function getColorTotalStock(inventory,colorName){
    if(!inventory)return null;
    const fsName=Object.keys(COLOR_NAME_MAP).find(k=>COLOR_NAME_MAP[k]===colorName)||colorName;
    return getTotalStockForColor(inventory,fsName)??getTotalStockForColor(inventory,colorName);
}

function buildImageArray(pd){
    if(Array.isArray(pd.images) && pd.images.length > 0){
        const filtered = pd.images.filter(Boolean);
        if(filtered.length > 0) return filtered;
    }
    if(pd.imageUrl || pd.image) return [pd.imageUrl || pd.image];
    return [placeholderShirt];
}

// ── Send Design via Email modal ───────────────────────────────────────────────
function SendDesignEmailModal({ storeEmail, productName, cartItemId, onClose, onSent, userName }) {
    const handleOpen = async () => {
        const subject = encodeURIComponent(`Design Submission - ${productName || 'T-Shirt'}`);
        const body    = encodeURIComponent(
            `Hi JG Printing,\n\nI would like to submit my design for the following order:\n\nCustomer Name: ${userName || 'N/A'}\nProduct: ${productName || 'T-Shirt'}\n\nPlease find my design file(s) attached.\n\nThank you!`
        );
        const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(storeEmail || '')}&su=${subject}&body=${body}`;
        const sentAt = new Date().toISOString();

        if (cartItemId) {
            try {
                await updateDoc(doc(db, 'carts', cartItemId), {
                    designEmailSent:   true,
                    designEmailSentAt: serverTimestamp(),
                    storeEmail:        storeEmail || '',
                });
            } catch (e) {
                console.warn('Could not write designEmailSent to cart item:', e);
            }
        }

        if (typeof onSent === 'function') {
            onSent({
                designEmailSent: true,
                designEmailSentAt: sentAt,
                storeEmail: storeEmail || '',
            });
        }

        window.open(gmailUrl, '_blank', 'noopener,noreferrer');
        onClose();
    };

    return (
        <div className="pd-email-modal-overlay" onClick={onClose}>
            <div className="pd-email-modal" onClick={e => e.stopPropagation()}>
                <div className="pd-email-modal-header">
                    <h3>Send Design via Email</h3>
                    <button className="pd-email-modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="pd-email-modal-body">
                    <p>This will open Gmail in a new tab with a pre-filled message. Attach your design files before sending.</p>
                    <div className="pd-email-modal-store">
                        <span className="pd-email-modal-label">Store Email:</span>
                        <span className="pd-email-modal-value">{storeEmail || 'Not configured'}</span>
                    </div>
                    <p className="pd-email-modal-hint">
                        Please attach your design files and include relevant order details (size, color, quantity, print location).
                    </p>
                </div>
                <div className="pd-email-modal-footer">
                    <button className="pd-email-modal-btn-cancel" onClick={onClose}>Cancel</button>
                    <button className="pd-email-modal-btn-send" onClick={handleOpen}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                            <polyline points="22,6 12,13 2,6"/>
                        </svg>
                        Open Gmail
                    </button>
                </div>
            </div>
        </div>
    );
}

// SHARED HOOK: useNoColorInventory
function useNoColorInventory(selectedProduct, productData) {
    const [noColorInventory, setNoColorInventory] = useState({});

    useEffect(() => {
        const productId = selectedProduct?.id || productData?.id;
        if (!productId) return;
        getDoc(doc(db, 'products', productId)).then(snap => {
            if (!snap.exists()) return;
            const inv = snap.data()?.inventory?.['_nocolor'] || snap.data()?.stockBySize || {};
            setNoColorInventory(inv);
        }).catch(() => {});
    }, [selectedProduct?.id, productData?.id]);

    const getSizeStockNoColor = useCallback((sizeKey) => {
        const displayKey = SIZE_KEY_TO_DISPLAY[sizeKey] || sizeKey;
        if (typeof noColorInventory[displayKey] === 'number') {
            return noColorInventory[displayKey];
        }
        const lower = displayKey.toLowerCase();
        const matchedKey = Object.keys(noColorInventory).find(k => k.toLowerCase() === lower);
        if (matchedKey !== undefined && typeof noColorInventory[matchedKey] === 'number') {
            return noColorInventory[matchedKey];
        }
        return null;
    }, [noColorInventory]);

    return { noColorInventory, getSizeStockNoColor };
}

// SHARED HANDLER: buildHandleQtyChange
function buildHandleQtyChange({ isPreview, getSizeStockNoColor, quantities, maxOrderQty, setQuantities }) {
    return (size, value) => {
        if (isPreview) return;
        const stock = getSizeStockNoColor(size);
        if (stock !== null && stock === 0) return;
        const num = Math.max(0, parseInt(value) || 0);
        const cappedByStock = (stock !== null && stock > 0)
            ? Math.min(num, stock)
            : Math.min(num, maxOrderQty);
        const currentTotal = Object.entries(quantities).reduce(
            (s, [k, v]) => k === size ? s : s + v, 0
        );
        const remainingQuota = Math.max(0, maxOrderQty - currentTotal);
        const allowed = Math.min(cappedByStock, remainingQuota);
        setQuantities(prev => ({ ...prev, [size]: allowed }));
    };
}

// JERSEY DETAILS VIEW
function JerseyDetailsView({ productData, isPreview, currentUser, onShowAuth }) {
    const navigate = useNavigate();
    const [mainImage, setMainImage] = useState(0);
    const [jerseyProducts,  setJerseyProducts]  = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);

    useEffect(() => {
        getProductsBySubcategory(SUBCATEGORY_JERSEY, { includeInactive: true }).then(({ products }) => {
            setJerseyProducts(products);
            if (productData.id) {
                const match = products.find(p => p.id === productData.id);
                if (match) setSelectedProduct(match);
                else if (products.length > 0) setSelectedProduct(products[0]);
            } else if (products.length > 0) {
                setSelectedProduct(products[0]);
            }
        });
    }, []); // eslint-disable-line

    const activeProduct  = selectedProduct || productData;
    const availableSizes = activeProduct.availableSizes || ALL_SIZES;
    const sizePrices     = activeProduct.sizePrices     || {};

    useEffect(() => {
        console.debug('[ProductDetails:Jersey] active product description state', {
            productId: activeProduct.id || null,
            productName: activeProduct.name,
            hasDescription: Boolean(activeProduct.description),
            descriptionLength: activeProduct.description?.length || 0,
        });
    }, [activeProduct.id, activeProduct.name, activeProduct.description]);

    const [teamName,  setTeamName]  = useState('');
    const [surname,   setSurname]   = useState('');
    const [number,    setNumber]    = useState('');
    const [note,      setNote]      = useState('');
    const [quantities, setQuantities] = useState(
        () => ALL_SIZES.reduce((a, s) => ({ ...a, [s]: 0 }), {})
    );
    const [validationError, setValidationError] = useState('');
    const [addingToCart,    setAddingToCart]     = useState(false);

    useEffect(() => {
        if (!selectedProduct) return;
        const sizes = selectedProduct.availableSizes || ALL_SIZES;
        setQuantities(prev => {
            const next = { ...prev };
            ALL_SIZES.forEach(s => {
                if (!sizes.includes(s)) next[s] = 0;
            });
            return next;
        });
    }, [selectedProduct]);

    const [maxOrderQty, setMaxOrderQty] = useState(50);
    useEffect(() => {
        getDoc(doc(db, 'settings', 'store')).then(snap => {
            if (!snap.exists()) return;
            const data = snap.data() || {};
            const limit = Number(data.deliveryConfig?.maxItemsPerOrder || data.orderLimits?.maxItemsPerOrder || 50);
            if (limit > 0) setMaxOrderQty(limit);
        }).catch(() => {});
    }, []);

    const { getSizeStockNoColor } = useNoColorInventory(selectedProduct, productData);

    const totalQuantity   = Object.values(quantities).reduce((s, q) => s + q, 0);
    const isOverOrderLimit = totalQuantity > maxOrderQty;

    const getPrice = (sizeKey) => sizePrices[sizeKey] ?? activeProduct.price ?? 0;
    const totalPrice = availableSizes.reduce((s, sz) => s + (quantities[sz] || 0) * getPrice(sz), 0);

    const handleQtyChange = buildHandleQtyChange({
        isPreview, getSizeStockNoColor, quantities, maxOrderQty, setQuantities,
    });

    const validate = () => {
        if (!teamName.trim())    return 'Please enter the Team Name.';
        if (!surname.trim())     return 'Please enter the Surname.';
        if (!number.trim())      return 'Please enter the Number.';
        if (totalQuantity === 0) return 'Please enter a quantity for at least one size.';
        if (isOverOrderLimit)    return `Maximum order quantity is ${maxOrderQty} items.`;
        return null;
    };

    const handleAddToCart = async () => {
        if (isPreview) return;
        setValidationError('');
        const err = validate();
        if (err) { setValidationError(err); return; }
        if (!currentUser) { onShowAuth(); return; }
        setAddingToCart(true);
        try {
            const productImages = buildImageArray(activeProduct);
            const cartItem = {
                name:        activeProduct.name,
                image:       productImages[0] || placeholderShirt,
                imageUrl:    activeProduct.imageUrl || activeProduct.image || productImages[0] || '',
                type:        activeProduct.name,
                subcategory: SUBCATEGORY_JERSEY,
                teamName,
                surname,
                number,
                quantities:  { ...quantities },
                quantity:    totalQuantity,
                price:       activeProduct.price || 0,
                sizePrices:  activeProduct.sizePrices || {},
                note,
                sizeBreakdown: Object.entries(quantities)
               .filter(([, q]) => q > 0)
               .map(([s, q]) => `${SIZE_KEY_TO_DISPLAY[s] || s}: ${q}`)
               .join(', ') || 'None',
            };
            console.log('DEBUG Jersey cartItem:', cartItem);
            const { error: cartErr } = await addCartItem(currentUser.uid, cartItem);
            if (cartErr) { setValidationError('Failed to add item to cart. Please try again.'); return; }
            navigate('/cart');
        } catch (e) {
            console.error(e);
            setValidationError('An unexpected error occurred.');
        } finally {
            setAddingToCart(false);
        }
    };

    const displayImages = buildImageArray(activeProduct);
    const safeMainImage = Math.min(mainImage, displayImages.length - 1);
    const goPrev = () => setMainImage(i => (i - 1 + displayImages.length) % displayImages.length);
    const goNext = () => setMainImage(i => (i + 1) % displayImages.length);

    return (
        <div className="product-details-container">
            <div className="page-header">
                <h1>{activeProduct.name}</h1>
                <p>Customize your jersey with team details and sizing.</p>
            </div>
            <div className="product-content">
                <div className="image-gallery">
                    <div className="main-image-wrapper">
                        {displayImages.length > 1 && (
                            <button className="main-image-arrow main-image-arrow--left" onClick={goPrev}>&#8249;</button>
                        )}
                        <div className="main-image">
                            <img src={displayImages[safeMainImage] || placeholderShirt} alt={activeProduct.name} />
                        </div>
                        {displayImages.length > 1 && (
                            <button className="main-image-arrow main-image-arrow--right" onClick={goNext}>&#8250;</button>
                        )}
                        {displayImages.length > 1 && (
                            <div className="main-image-dots">
                                {displayImages.map((_, i) => (
                                    <button key={i} className={`main-image-dot ${safeMainImage === i ? 'active' : ''}`} onClick={() => setMainImage(i)} />
                                ))}
                            </div>
                        )}
                    </div>
                    {displayImages.length > 1 && (
                        <div className="thumbnails">
                            {displayImages.map((img, i) => (
                                <div key={i} className={`thumbnail ${safeMainImage === i ? 'active' : ''}`} onClick={() => setMainImage(i)}>
                                    <img src={img} alt={`View ${i + 1}`} />
                                </div>
                            ))}
                        </div>
                    )}
                    {!isPreview && (
                        <div className="note-section">
                            <label>Note: <span className="optional">Optional</span></label>
                            <textarea placeholder="Additional instructions…" value={note} onChange={e => setNote(e.target.value)} />
                        </div>
                    )}
                </div>
                <div className="product-config">
                    <div className="config-group">
                        <label>Type</label>
                        <select
                            value={selectedProduct?.id || ''}
                            onChange={e => {
                                if (isPreview) return;
                                const found = jerseyProducts.find(p => p.id === e.target.value);
                                if (found) { setSelectedProduct(found); setMainImage(0); }
                            }}
                            disabled={isPreview}
                        >
                            <option value="" disabled hidden>Select type</option>
                            {jerseyProducts.map(p => (
                                <option key={p.id} value={p.id} disabled={p.status === 'inactive'}>
                                    {p.name}{p.status === 'inactive' ? ' (Unavailable)' : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="config-group">
                        <div className="pd-polo-design-info">
                            <span className="pd-polo-design-icon">🎨</span>
                            <div>
                                <p className="pd-polo-design-title">Design by JG Printing</p>
                                <p className="pd-polo-design-desc">
                                    Basketball jersey designs are pre-made and provided by JG Printing.
                                    You may add any special instructions in the note below.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="config-group">
                        <label>Team Name <span style={{color:'#e53935',fontSize:'12px'}}>(printed on front)</span></label>
                        <input type="text" className="pd-text-input" placeholder="e.g. Warriors" value={teamName}
                            onChange={e => { if (!isPreview) setTeamName(e.target.value); }} disabled={isPreview} />
                    </div>
                    <div className="config-group">
                        <label>Surname <span style={{color:'#e53935',fontSize:'12px'}}>(printed on back)</span></label>
                        <input type="text" className="pd-text-input" placeholder="e.g. Santos" value={surname}
                            onChange={e => { if (!isPreview) setSurname(e.target.value); }} disabled={isPreview} />
                    </div>
                    <div className="config-group">
                        <label>Number <span style={{color:'#e53935',fontSize:'12px'}}>(printed on front and back)</span></label>
                        <input type="text" inputMode="numeric" pattern="[0-9]*" className="pd-text-input" placeholder="e.g. 7" value={number}
                            onChange={e => { if (isPreview) return; setNumber(e.target.value.replace(/[^0-9]/g, '')); }} disabled={isPreview} />
                    </div>
                    <div className="config-group">
                        <label>Quantity <span className="info-icon">ⓘ<span className="info-tooltip">Enter quantity per size</span></span></label>
                        <div className="pd-order-limit-bar">
                            <span className={`pd-order-limit-text ${isOverOrderLimit ? 'pd-order-limit-text--over' : totalQuantity > maxOrderQty * 0.8 ? 'pd-order-limit-text--warn' : ''}`}>
                                {totalQuantity} / {maxOrderQty} items
                            </span>
                            {isOverOrderLimit && <span className="pd-order-limit-badge">Over limit</span>}
                        </div>
                        <div className="quantity-grid">
                            {availableSizes.map(size => {
                                const price = getPrice(size);
                                const st    = getSizeStockNoColor(size);
                                const oos   = st !== null && st === 0;
                                const low   = st !== null && st > 0 && st <= LOW_STOCK_THRESHOLD;
                                return (
                                    <div key={size} className={`quantity-item${oos ? ' quantity-item--oos' : low ? ' quantity-item--low' : ''}`}>
                                        <label>
                                            {SIZE_KEY_TO_DISPLAY[size] || size}
                                            {price > 0 && !oos && <span className="size-price-tag">₱{price}</span>}
                                            {oos && <span className="size-oos-tag">Out of stock</span>}
                                            {low && !oos && <span className="size-low-tag">Only {st} left</span>}
                                        </label>
                                        <div className="quantity-stepper">
                                            <button className="quantity-stepper-btn" disabled={isPreview || oos || (quantities[size] || 0) <= 0}
                                                onClick={() => handleQtyChange(size, (quantities[size] || 0) - 1)}>−</button>
                                            <input type="number" min="0" max={st !== null && st > 0 ? st : maxOrderQty}
                                                value={oos ? '' : (quantities[size] === 0 ? '' : quantities[size])}
                                                placeholder={oos ? '—' : '0'} disabled={isPreview || oos}
                                                onChange={e => {
                                                    if (isPreview) return;
                                                    const v = e.target.value;
                                                    if (v === '') { handleQtyChange(size, 0); return; }
                                                    const n = parseInt(v);
                                                    if (!isNaN(n) && n >= 0) handleQtyChange(size, n);
                                                }}
                                                className={`quantity-input${oos ? ' quantity-input--oos' : low ? ' quantity-input--low' : isOverOrderLimit ? ' quantity-input--over' : ''}`}
                                            />
                                            <button className="quantity-stepper-btn" disabled={isPreview || oos || totalQuantity >= maxOrderQty || (st !== null && st > 0 && (quantities[size] || 0) >= st)}
                                                onClick={() => handleQtyChange(size, (quantities[size] || 0) + 1)}>+</button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className="total-section">
                        <span className="total-label">Total:</span>
                        <span className="total-amount">₱ {totalPrice.toLocaleString()}</span>
                    </div>
                    {!isPreview && isOverOrderLimit && <div className="pd-order-limit-error">⚠ Maximum order is {maxOrderQty} items.</div>}
                    {!isPreview && validationError && <div className="error-message">{validationError}</div>}
                    <button className="add-to-cart-btn" onClick={handleAddToCart}
                        disabled={addingToCart || isPreview || isOverOrderLimit || totalQuantity === 0}
                        style={isPreview ? { backgroundColor: '#aaa', cursor: 'not-allowed' } : {}}>
                        {isPreview ? 'Not Available in Preview Mode' : addingToCart ? 'Adding…' : 'Add to Cart'}
                    </button>
                </div>
            </div>

            {activeProduct.description && (
                <ProductDescriptionBlock 
                    description={activeProduct.description}
                    title={`About ${activeProduct.name || 'This Product'}`}
                    highlightColor="#111"
                />
            )}
        </div>
    );
}

// POLO SHIRT DETAILS VIEW
function PoloDetailsView({ productData, isPreview, currentUser, onShowAuth }) {
    const navigate = useNavigate();
    const [mainImage, setMainImage] = useState(0);
    const [poloProducts,    setPoloProducts]    = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);

    useEffect(() => {
        getProductsBySubcategory(SUBCATEGORY_POLO, { includeInactive: true }).then(({ products }) => {
            setPoloProducts(products);
            if (productData.id) {
                const match = products.find(p => p.id === productData.id);
                if (match) setSelectedProduct(match);
                else if (products.length > 0) setSelectedProduct(products[0]);
            } else if (products.length > 0) {
                setSelectedProduct(products[0]);
            }
        });
    }, []); // eslint-disable-line

    const activeProduct  = selectedProduct || productData;
    const availableSizes = activeProduct.availableSizes || ALL_SIZES;
    const sizePrices     = activeProduct.sizePrices     || {};

    useEffect(() => {
        console.debug('[ProductDetails:Polo] active product description state', {
            productId: activeProduct.id || null,
            productName: activeProduct.name,
            hasDescription: Boolean(activeProduct.description),
            descriptionLength: activeProduct.description?.length || 0,
        });
    }, [activeProduct.id, activeProduct.name, activeProduct.description]);

    const [note, setNote]         = useState('');
    const [quantities, setQuantities] = useState(
        () => ALL_SIZES.reduce((a, s) => ({ ...a, [s]: 0 }), {})
    );
    const [validationError, setValidationError] = useState('');
    const [addingToCart,    setAddingToCart]     = useState(false);

    useEffect(() => {
        if (!selectedProduct) return;
        const sizes = selectedProduct.availableSizes || ALL_SIZES;
        setQuantities(prev => {
            const next = { ...prev };
            ALL_SIZES.forEach(s => {
                if (!sizes.includes(s)) next[s] = 0;
            });
            return next;
        });
    }, [selectedProduct]);

    const [maxOrderQty, setMaxOrderQty] = useState(50);
    useEffect(() => {
        getDoc(doc(db, 'settings', 'store')).then(snap => {
            if (!snap.exists()) return;
            const data = snap.data() || {};
            const limit = Number(data.deliveryConfig?.maxItemsPerOrder || data.orderLimits?.maxItemsPerOrder || 50);
            if (limit > 0) setMaxOrderQty(limit);
        }).catch(() => {});
    }, []);

    const { getSizeStockNoColor } = useNoColorInventory(selectedProduct, productData);

    const totalQuantity    = Object.values(quantities).reduce((s, q) => s + q, 0);
    const isOverOrderLimit = totalQuantity > maxOrderQty;

    const getPrice   = (sizeKey) => sizePrices[sizeKey] ?? activeProduct.price ?? 0;
    const totalPrice = availableSizes.reduce((s, sz) => s + (quantities[sz] || 0) * getPrice(sz), 0);

    const handleQtyChange = buildHandleQtyChange({
        isPreview, getSizeStockNoColor, quantities, maxOrderQty, setQuantities,
    });

    const validate = () => {
        if (totalQuantity === 0) return 'Please enter a quantity for at least one size.';
        if (isOverOrderLimit)   return `Maximum order quantity is ${maxOrderQty} items.`;
        return null;
    };

    const handleAddToCart = async () => {
        if (isPreview) return;
        setValidationError('');
        const err = validate();
        if (err) { setValidationError(err); return; }
        if (!currentUser) { onShowAuth(); return; }
        setAddingToCart(true);
        try {
            const productImages = buildImageArray(activeProduct);
            const cartItem = {
                name:        activeProduct.name,
                image:       productImages[0] || placeholderShirt,
                imageUrl:    activeProduct.imageUrl || activeProduct.image || productImages[0] || '',
                type:        activeProduct.name,
                subcategory: SUBCATEGORY_POLO,
                quantities:  { ...quantities },
                quantity:    totalQuantity,
                price:       activeProduct.price || 0,
                sizePrices:  activeProduct.sizePrices || {},
                note,
                sizeBreakdown: Object.entries(quantities)
                .filter(([, q]) => q > 0)
                .map(([s, q]) => `${SIZE_KEY_TO_DISPLAY[s] || s}: ${q}`)
                .join(', ') || 'None',
            };
            const { error: cartErr } = await addCartItem(currentUser.uid, cartItem);
            if (cartErr) { setValidationError('Failed to add item to cart. Please try again.'); return; }
            navigate('/cart');
        } catch (e) {
            console.error(e);
            setValidationError('An unexpected error occurred.');
        } finally {
            setAddingToCart(false);
        }
    };

    const displayImages = buildImageArray(activeProduct);
    const safeMainImage = Math.min(mainImage, displayImages.length - 1);
    const goPrev = () => setMainImage(i => (i - 1 + displayImages.length) % displayImages.length);
    const goNext = () => setMainImage(i => (i + 1) % displayImages.length);

    return (
        <div className="product-details-container">
            <div className="page-header">
                <h1>{activeProduct.name}</h1>
                <p>Select your size and quantity to order.</p>
            </div>
            <div className="product-content">
                <div className="image-gallery">
                    <div className="main-image-wrapper">
                        {displayImages.length > 1 && (
                            <button className="main-image-arrow main-image-arrow--left" onClick={goPrev}>&#8249;</button>
                        )}
                        <div className="main-image">
                            <img src={displayImages[safeMainImage] || placeholderShirt} alt={activeProduct.name} />
                        </div>
                        {displayImages.length > 1 && (
                            <button className="main-image-arrow main-image-arrow--right" onClick={goNext}>&#8250;</button>
                        )}
                        {displayImages.length > 1 && (
                            <div className="main-image-dots">
                                {displayImages.map((_, i) => (
                                    <button key={i} className={`main-image-dot ${safeMainImage === i ? 'active' : ''}`} onClick={() => setMainImage(i)} />
                                ))}
                            </div>
                        )}
                    </div>
                    {displayImages.length > 1 && (
                        <div className="thumbnails">
                            {displayImages.map((img, i) => (
                                <div key={i} className={`thumbnail ${safeMainImage === i ? 'active' : ''}`} onClick={() => setMainImage(i)}>
                                    <img src={img} alt={`View ${i + 1}`} />
                                </div>
                            ))}
                        </div>
                    )}
                    {!isPreview && (
                        <div className="note-section">
                            <label>Note: <span className="optional">Optional</span></label>
                            <textarea placeholder="Additional instructions…" value={note} onChange={e => setNote(e.target.value)} />
                        </div>
                    )}
                </div>
                <div className="product-config">
                    <div className="config-group">
                        <label>Type</label>
                        <select value={selectedProduct?.id || ''}
                            onChange={e => {
                                if (isPreview) return;
                                const found = poloProducts.find(p => p.id === e.target.value);
                                if (found) { setSelectedProduct(found); setMainImage(0); }
                            }}
                            disabled={isPreview}>
                            <option value="" disabled hidden>Select type</option>
                            {poloProducts.map(p => (
                                <option key={p.id} value={p.id} disabled={p.status === 'inactive'}>
                                    {p.name}{p.status === 'inactive' ? ' (Unavailable)' : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="config-group">
                        <div className="pd-polo-design-info">
                            <span className="pd-polo-design-icon">🎨</span>
                            <div>
                                <p className="pd-polo-design-title">Design by JG Printing</p>
                                <p className="pd-polo-design-desc">
                                    Polo shirt designs are pre-made and provided by JG Printing.
                                    You may add any special instructions in the note below.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="config-group">
                        <label>Quantity <span className="info-icon">ⓘ<span className="info-tooltip">Enter quantity per size</span></span></label>
                        <div className="pd-order-limit-bar">
                            <span className={`pd-order-limit-text ${isOverOrderLimit ? 'pd-order-limit-text--over' : totalQuantity > maxOrderQty * 0.8 ? 'pd-order-limit-text--warn' : ''}`}>
                                {totalQuantity} / {maxOrderQty} items
                            </span>
                            {isOverOrderLimit && <span className="pd-order-limit-badge">Over limit</span>}
                        </div>
                        <div className="quantity-grid">
                            {availableSizes.map(size => {
                                const price = getPrice(size);
                                const st    = getSizeStockNoColor(size);
                                const oos   = st !== null && st === 0;
                                const low   = st !== null && st > 0 && st <= LOW_STOCK_THRESHOLD;
                                return (
                                    <div key={size} className={`quantity-item${oos ? ' quantity-item--oos' : low ? ' quantity-item--low' : ''}`}>
                                        <label>
                                            {SIZE_KEY_TO_DISPLAY[size] || size}
                                            {price > 0 && !oos && <span className="size-price-tag">₱{price}</span>}
                                            {oos && <span className="size-oos-tag">Out of stock</span>}
                                            {low && !oos && <span className="size-low-tag">Only {st} left</span>}
                                        </label>
                                        <div className="quantity-stepper">
                                            <button className="quantity-stepper-btn" disabled={isPreview || oos || (quantities[size] || 0) <= 0}
                                                onClick={() => handleQtyChange(size, (quantities[size] || 0) - 1)}>−</button>
                                            <input type="number" min="0" max={st !== null && st > 0 ? st : maxOrderQty}
                                                value={oos ? '' : (quantities[size] === 0 ? '' : quantities[size])}
                                                placeholder={oos ? '—' : '0'} disabled={isPreview || oos}
                                                onChange={e => {
                                                    if (isPreview) return;
                                                    const v = e.target.value;
                                                    if (v === '') { handleQtyChange(size, 0); return; }
                                                    const n = parseInt(v);
                                                    if (!isNaN(n) && n >= 0) handleQtyChange(size, n);
                                                }}
                                                className={`quantity-input${oos ? ' quantity-input--oos' : low ? ' quantity-input--low' : isOverOrderLimit ? ' quantity-input--over' : ''}`}
                                            />
                                            <button className="quantity-stepper-btn" disabled={isPreview || oos || totalQuantity >= maxOrderQty || (st !== null && st > 0 && (quantities[size] || 0) >= st)}
                                                onClick={() => handleQtyChange(size, (quantities[size] || 0) + 1)}>+</button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className="total-section">
                        <span className="total-label">Total:</span>
                        <span className="total-amount">₱ {totalPrice.toLocaleString()}</span>
                    </div>
                    {!isPreview && isOverOrderLimit && <div className="pd-order-limit-error">⚠ Maximum order is {maxOrderQty} items.</div>}
                    {!isPreview && validationError && <div className="error-message">{validationError}</div>}
                    <button className="add-to-cart-btn" onClick={handleAddToCart}
                        disabled={addingToCart || isPreview || isOverOrderLimit || totalQuantity === 0}
                        style={isPreview ? { backgroundColor: '#aaa', cursor: 'not-allowed' } : {}}>
                        {isPreview ? 'Not Available in Preview Mode' : addingToCart ? 'Adding…' : 'Add to Cart'}
                    </button>
                </div>
            </div>

            {activeProduct.description && (
                <ProductDescriptionBlock 
                    description={activeProduct.description}
                    title={`About ${activeProduct.name || 'This Product'}`}
                    highlightColor="#111"
                />
            )}
        </div>
    );
}

// MAIN COMPONENT — detects subcategory and renders correct view
function ProductDetails(){
    const navigate  = useNavigate();
    const location  = useLocation();
    const { currentUser } = useAuth();
    const isPreview = usePreviewMode();

    const productData =
        location.state?.product ||
        location.state?.productConfig?.product ||
        { name:'T-Shirt', price:85, image:placeholderShirt, category:'T-Shirt', subcategory: SUBCATEGORY_TSHIRT };

    const subcategory = productData.subcategory || productData.category || null;
    const isTShirt = subcategory === SUBCATEGORY_TSHIRT;
    const isJersey = subcategory === SUBCATEGORY_JERSEY;
    const isPolo   = subcategory === SUBCATEGORY_POLO;
    const isClothing = isTShirt || isJersey || isPolo;

    const [showAuthModal, setShowAuthModal] = useState(false);

    if (isTShirt) {
        return <TShirtDetailsView
            productData={productData} location={location} navigate={navigate}
            currentUser={currentUser} isPreview={isPreview}
            showAuthModal={showAuthModal} setShowAuthModal={setShowAuthModal}
        />;
    }

    if (isJersey) {
        return (
            <>
                {!isPreview && showAuthModal && <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />}
                <JerseyDetailsView productData={productData} isPreview={isPreview} currentUser={currentUser} onShowAuth={() => setShowAuthModal(true)} />
            </>
        );
    }

    if (isPolo) {
        return (
            <>
                {!isPreview && showAuthModal && <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />}
                <PoloDetailsView productData={productData} isPreview={isPreview} currentUser={currentUser} onShowAuth={() => setShowAuthModal(true)} />
            </>
        );
    }

    if (!isClothing) {
        return (
            <>
                {!isPreview && showAuthModal && <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />}
                <DynamicProductDetailsView productData={productData} isPreview={isPreview} currentUser={currentUser} onShowAuth={() => setShowAuthModal(true)} />
            </>
        );
    }

    return null;
}

// T-SHIRT DETAILS VIEW
function TShirtDetailsView({ productData, location, navigate, currentUser, isPreview, showAuthModal, setShowAuthModal }) {
    const returnedConfig  = location.state?.productConfig || {};
    const returnedDesigns = location.state?.updatedDesigns || {};
    const returningFromEditor = 'updatedDesigns' in (location.state || {});

    const [productImages,    setProductImages]    = useState(() => buildImageArray(productData));
    const [productInventory, setProductInventory] = useState(productData.inventory || null);
    const [tshirtProducts,   setTshirtProducts]   = useState([]);
    const [shirtSizePrices,  setShirtSizePrices]  = useState({});
    const [maxOrderQty,      setMaxOrderQty]      = useState(50);
    const [storeEmail,       setStoreEmail]       = useState('');
    const [showEmailModal,   setShowEmailModal]   = useState(false);
    const [rawOriginalURLs,  setRawOriginalURLs]  = useState(() => location.state?.rawOriginalURLs || {});
    const [savedTransforms,  setSavedTransforms]  = useState(() => location.state?.savedTransforms || {});
    const [editorSettings,   setEditorSettings]   = useState(() => location.state?.editorSettings || {});
    const [lastCartItemId,   setLastCartItemId]   = useState(null);
    const [designEmailMeta,  setDesignEmailMeta]  = useState(null);
    const [userName, setUserName] = useState('');
    const [selectedType,   setSelectedType]   = useState(() => returnedConfig.selectedType || '');
    const designEmailStorageKey = useMemo(() => {
        const productKey = String(productData.id || productData.name || 'tshirt').trim();
        const typeKey = String(selectedType || productData.name || 'tshirt').trim();
        return `pd:design-email:${productKey}:${typeKey}`;
    }, [productData.id, productData.name, selectedType]);

    useEffect(() => {
        getDoc(doc(db, 'settings', 'store')).then(snap => {
            if (!snap.exists()) return;
            const data = snap.data() || {};
            const limit = Number(data.deliveryConfig?.maxItemsPerOrder || data.orderLimits?.maxItemsPerOrder || 50);
            if (limit > 0) setMaxOrderQty(limit);
            if (data.emailAddress) setStoreEmail(data.emailAddress);
        }).catch(() => {});
    }, []);
    
    useEffect(() => {
        if (!currentUser?.uid) return;
        getDoc(doc(db, 'users', currentUser.uid)).then(snap => {
            if (!snap.exists()) return;
            const data = snap.data() || {};
            const name = [data.firstName, data.lastName].filter(Boolean).join(' ').trim();
            if (name) setUserName(name);
        }).catch(() => {});
    }, [currentUser?.uid]);

    useEffect(() => {
        let mounted = true;
        getTShirtProducts({ includeInactive: true }).then(({ products: fetched, sizePrices, fromCache }) => {
            if (!mounted || fromCache) return;
            if (fetched && fetched.length > 0) {
                setTshirtProducts(fetched);
                if (productData.id) {
                    const match = fetched.find(p => p.id === productData.id);
                    if (match) {
                        if (match.inventory) setProductInventory(match.inventory);
                        if (!returningFromEditor) {
                            const fresh = buildImageArray(match);
                            if (fresh.length > 0 && fresh[0] !== placeholderShirt) setProductImages(fresh);
                        }
                    }
                }
            }
            if (sizePrices && Object.keys(sizePrices).length > 0) setShirtSizePrices(sizePrices);
        });
        return () => { mounted = false; };
    }, []); // eslint-disable-line

    useEffect(() => {
        if (!returningFromEditor) return;
        if (location.state?.updatedDesigns) {
            setDesigns(prev => ({ ...prev, ...location.state.updatedDesigns }));
        }
        if (location.state?.rawOriginalURLs) {
            setRawOriginalURLs(prev => ({ ...prev, ...location.state.rawOriginalURLs }));
        }
        if (location.state?.savedTransforms) {
            setSavedTransforms(prev => ({ ...prev, ...location.state.savedTransforms }));
        }
        if (location.state?.editorSettings) {
            setEditorSettings(prev => ({ ...prev, ...location.state.editorSettings }));
        }
        if (typeof location.state?.currentStep === 'number') {
            setCurrentStep(location.state.currentStep);
        }
    }, [location.state, returningFromEditor]);

    useEffect(() => {
        try {
            const storedValue = sessionStorage.getItem(designEmailStorageKey);
            if (!storedValue) {
                setDesignEmailMeta(null);
                return;
            }
            const parsedValue = JSON.parse(storedValue);
            if (parsedValue?.designEmailSent) {
                setDesignEmailMeta(parsedValue);
            } else {
                setDesignEmailMeta(null);
            }
        } catch {
            setDesignEmailMeta(null);
        }
    }, [designEmailStorageKey]);

    const getInitialType = useCallback(() => {
        if (productData.id) {
            const m = tshirtProducts.find(p => p.id === productData.id);
            if (m) return m.name;
        }
        const nm = tshirtProducts.find(p => p.name === productData.name);
        if (nm) return nm.name;
        return returnedConfig.selectedType || '';
    }, [tshirtProducts, productData.id, productData.name, returnedConfig.selectedType]);

    const [openGuidelines, setOpenGuidelines] = useState({ 'Men/Unisex': true, 'Women': false });
    const [mainImage,      setMainImage]      = useState(0);
    const [activeTab,      setActiveTab]      = useState('details');
    const [note,           setNote]           = useState(returnedConfig.note || '');
    const [addingToCart,   setAddingToCart]   = useState(false);
    const [validationError,setValidationError]= useState('');
    const [uploadModalOpen,setUploadModalOpen]= useState(false);
    const [uploadingSide,  setUploadingSide]  = useState('front');
    const [uploadingLocation, setUploadingLocation] = useState('');
    const [selectedColor,  setSelectedColor]  = useState(returnedConfig.selectedColor || '');
    const [selectedShape,  setSelectedShape]  = useState(returnedConfig.selectedShape || 'circle');
    const [selectedFrontOptions, setSelectedFrontOptions] = useState(returnedConfig.selectedFrontOptions || []);
    const [selectedBackOptions,  setSelectedBackOptions]  = useState(returnedConfig.selectedBackOptions  || []);
    const [selectedSleeves,      setSelectedSleeves]      = useState(returnedConfig.selectedSleeves      || []);
    const [frontDropdownOpen, setFrontDropdownOpen] = useState(false);
    const [backDropdownOpen,  setBackDropdownOpen]  = useState(false);
    const frontRef = useRef(null);
    const backRef  = useRef(null);
    const [designs,    setDesigns]    = useState(returnedDesigns || {});
    const [quantities, setQuantities] = useState(returnedConfig.quantities || ALL_SIZES.reduce((a,s)=>({...a,[s]:0}),{}));
    const [frontLimitWarning, setFrontLimitWarning] = useState(false);
    const [currentStep, setCurrentStep] = useState(() => location.state?.currentStep ?? 0);

    // Step-by-step configuration
    const steps = [
        { id: 'type', title: 'Select Type', icon: '👕' },
        { id: 'color', title: 'Choose Color', icon: '🎨' },
        { id: 'shape', title: 'Design Shape', icon: '⭕' },
        { id: 'location', title: 'Print Location', icon: '📍' },
        { id: 'upload', title: 'Upload Design', icon: '⬆️' },
        { id: 'quantity', title: 'Quantity', icon: '📦' },
    ];

    useEffect(() => {
        if (tshirtProducts.length === 0) return;
        if (selectedType) return;
        const resolved = getInitialType();
        if (resolved) setSelectedType(resolved);
    }, [tshirtProducts, getInitialType, selectedType]);

    const selectedProduct  = tshirtProducts.find(p => p.name === selectedType) || null;
    const activeDescription = selectedProduct?.description || productData.description || '';
    const activeDescriptionTitle = selectedProduct?.name || productData.name || 'This Product';
    const availableSizes   = selectedProduct
        ? (selectedProduct.availableSizes?.length > 0 ? selectedProduct.availableSizes : ALL_SIZES)
        : ALL_SIZES;
    const totalQuantity    = Object.values(quantities).reduce((s, q) => s + q, 0);
    const backOptions      = selectedShape === 'rectangle' ? BACK_OPTIONS_RECTANGLE : BACK_OPTIONS_CIRCLE;
    const blockedFront     = getBlockedFrontOptions(selectedFrontOptions);
    const blockedBack      = getBlockedBackOptions(selectedBackOptions);
    const allActiveLocations = [
        ...selectedFrontOptions,
        ...selectedBackOptions,
        ...(selectedSleeves.includes('left')  ? ['Left Sleeve']  : []),
        ...(selectedSleeves.includes('right') ? ['Right Sleeve'] : []),
    ];

    const colorOptions = (selectedProduct?.colors || []).map(rawName => {
        const key = COLOR_NAME_MAP[rawName] || rawName.toLowerCase();
        const hex = COLOR_HEX_MAP[key] || '#cccccc';
        return { rawName, key, hex };
    });

    const colorPrices = selectedProduct?.colorPrices || {};
    const selectedColorSurcharge = selectedColor
        ? (colorPrices[selectedColor] || colorPrices[
            Object.keys(COLOR_NAME_MAP).find(k => COLOR_NAME_MAP[k] === selectedColor)
        ] || 0)
        : 0;

    const colorTotalStock   = getColorTotalStock(productInventory, selectedColor);
    const isColorOutOfStock = colorTotalStock !== null && colorTotalStock === 0;
    const getSizeStockForUI = useCallback((key) => getSizeStock(productInventory, selectedColor, key), [productInventory, selectedColor]);

    const getEffectiveSizePrices = () => {
        if (selectedProduct?.sizePrices && Object.keys(selectedProduct.sizePrices).length > 0)
            return selectedProduct.sizePrices;
        return shirtSizePrices[selectedType] || {};
    };
    const sizePricesForDisplay = getEffectiveSizePrices();

    const shirtSubtotal  = selectedType ? ALL_SIZES.reduce((s, sz) => {
        const qty       = quantities[sz] || 0;
        const sizePrice = sizePricesForDisplay[sz] || 0;
        return s + (qty * (sizePrice + selectedColorSurcharge));
    }, 0) : 0;
    const imprintSubtotal = calcImprintTotal(selectedFrontOptions, selectedBackOptions, selectedSleeves, selectedShape) * totalQuantity;
    const totalPrice      = shirtSubtotal + imprintSubtotal;
    const isOverOrderLimit = totalQuantity > maxOrderQty;

    useEffect(() => {
        const h = (e) => {
            if (frontRef.current && !frontRef.current.contains(e.target)) setFrontDropdownOpen(false);
            if (backRef.current  && !backRef.current.contains(e.target))  setBackDropdownOpen(false);
        };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    const prevTypeRef = useRef(returnedConfig.selectedType || null);

    useEffect(() => {
        if (!selectedType) return;
        if (prevTypeRef.current === selectedType) return;
        prevTypeRef.current = selectedType;
        if (!selectedProduct) { setSelectedColor(''); return; }
        if (!selectedProduct.colors || selectedProduct.colors.length === 0) {
            setSelectedColor('white');
            return;
        }
        const validKeys = selectedProduct.colors.map(c => COLOR_NAME_MAP[c] || c.toLowerCase());
        if (validKeys.includes(selectedColor)) return;
        setSelectedColor(validKeys[0] || '');
    }, [selectedType, selectedProduct, selectedColor]);

    useEffect(() => {
        if (returningFromEditor) return;
        const avail = new Set(availableSizes);
        setQuantities(prev => {
            const n = { ...prev };
            ALL_SIZES.forEach(s => { if (!avail.has(s)) n[s] = 0; });
            return n;
        });
    }, [selectedType, availableSizes, returningFromEditor]);

    useEffect(() => {
        setSelectedBackOptions(prev => prev.filter(o => backOptions.includes(o)));
    }, [selectedShape, backOptions]);

    useEffect(() => {
        if (!productInventory || !selectedColor) return;
        setQuantities(prev => {
            const n = { ...prev };
            ALL_SIZES.forEach(sz => { const st = getSizeStockForUI(sz); if (st !== null && st === 0) n[sz] = 0; });
            return n;
        });
    }, [selectedColor, productInventory, getSizeStockForUI]);

    useEffect(() => {
        const activeSet = new Set([
            ...selectedFrontOptions, ...selectedBackOptions,
            ...(selectedSleeves.includes('left')  ? ['Left Sleeve']  : []),
            ...(selectedSleeves.includes('right') ? ['Right Sleeve'] : []),
        ]);
        setDesigns(prev => { const n = { ...prev }; Object.keys(n).forEach(loc => { if (!activeSet.has(loc)) delete n[loc]; }); return n; });
        setRawOriginalURLs(prev => { const n = { ...prev }; Object.keys(n).forEach(loc => { if (!activeSet.has(loc)) delete n[loc]; }); return n; });
        setSavedTransforms(prev => { const n = { ...prev }; Object.keys(n).forEach(loc => { if (!activeSet.has(loc)) delete n[loc]; }); return n; });
    }, [selectedFrontOptions, selectedBackOptions, selectedSleeves]);

    useEffect(() => {
        if (returningFromEditor) return;
        const inc = location.state?.product; if (!inc) return;
        if (inc.inventory) setProductInventory(inc.inventory);
        const fresh = buildImageArray(inc); if (fresh.length > 0) setProductImages(fresh);
        setSelectedColor(''); setSelectedShape('circle');
        setSelectedFrontOptions([]); setSelectedBackOptions([]); setSelectedSleeves([]);
        setDesigns({}); setQuantities(ALL_SIZES.reduce((a,s) => ({...a,[s]:0}), {}));
        setRawOriginalURLs({}); setSavedTransforms({});
        setNote(''); setValidationError(''); setMainImage(0);
        prevTypeRef.current = null;
    }, [location.state?.product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        console.debug('[ProductDetails:TShirt] active product description state', {
            productId: selectedProduct?.id || productData.id || null,
            productName: activeDescriptionTitle,
            hasDescription: Boolean(activeDescription),
            descriptionLength: activeDescription.length,
        });
    }, [selectedProduct?.id, productData.id, activeDescriptionTitle, activeDescription]);

    const STEP_TIPS = [
    {
        title: '💡 Choosing a Type',
        content: (
            <ul>
                <li><strong>Crew Neck</strong> — Classic round neckline, great for casual and sporty looks.</li>
                <li><strong>V-Neck</strong> — Slimmer, more dressed-up feel.</li>
                <li>Not sure? Crew Neck is the most popular choice for custom prints.</li>
            </ul>
        ),
    },
    {
        title: '🎨 Picking a Color',
        content: (
            <ul>
                <li>Lighter shirts show designs more vividly.</li>
                <li>Dark shirts may slightly affect how bright the print appears.</li>
                <li>Colors with a surcharge (+₱) cost a little more per piece.</li>
            </ul>
        ),
    },
    {
        title: '⭕ About Design Shape',
        content: (
            <ul>
                <li><strong>Circle</strong> — Best for logos, emblems, and round artwork.</li>
                <li><strong>Rectangle</strong> — Better for text, wide designs, or landscape artwork.</li>
                <li>The shape affects which print locations are available.</li>
            </ul>
        ),
    },
    {
        title: '📍 Choosing Print Locations',
        content: (
            <ul>
                <li><strong>Full Front</strong> is the most popular — great for bold statements.</li>
                <li><strong>Left Chest</strong> is subtle and professional.</li>
                <li>You can combine up to 3 front and 2 back locations.</li>
                <li>Each location requires its own design upload in the next step.</li>
            </ul>
        ),
    },
    {
        title: '⬆️ Uploading Your Design',
        content: (
            <ul>
                <li>Use <strong>PNG with transparent background</strong> for best results.</li>
                <li>Minimum resolution: <strong>350 DPI</strong>, color mode: <strong>RGB</strong>.</li>
                <li>Max file size: <strong>75MB</strong>.</li>
                <li>White backgrounds in your design <em>will</em> be printed — use transparency instead.</li>
            </ul>
        ),
    },
];

const goPrev = () => setMainImage(i => (i - 1 + productImages.length) % productImages.length);
const goNext = () => setMainImage(i => (i + 1) % productImages.length);

    const handleQuantityChange = (size, value) => {
        if (isPreview) return;
        const stock  = getSizeStockForUI(size);
        if (stock !== null && stock === 0) return;
        const num    = parseInt(value) || 0;
        const capped = (stock !== null && stock > 0) ? Math.min(num, stock) : num;
        const currentTotal = Object.entries(quantities).reduce((s,[k,v]) => k===size?s:s+v, 0);
        const allowed = Math.min(capped, Math.max(0, maxOrderQty - currentTotal));
        setQuantities(prev => ({ ...prev, [size]: allowed }));
    };

    const toggleFrontOption = (opt) => {
        if (isPreview) return;
        setSelectedFrontOptions(prev => {
            if (prev.includes(opt)) return prev.filter(o => o !== opt);
            if (prev.length >= MAX_FRONT_LOCATIONS) {
                setFrontLimitWarning(true);
                setTimeout(() => setFrontLimitWarning(false), 3000);
                return prev;
            }
            return [...prev, opt];
        });
    };

    const toggleBackOption = (opt) => {
        if (isPreview) return;
        setSelectedBackOptions(prev => {
            if (prev.includes(opt)) return prev.filter(o => o !== opt);
            if (prev.length >= MAX_BACK_LOCATIONS) return prev;
            return [...prev, opt];
        });
    };

    const handleOpenUpload    = (side, loc = '') => { setUploadingSide(side); setUploadingLocation(loc); setUploadModalOpen(true); };
    const handleUploadConfirm = (loc, file) => {
        setDesigns(prev => ({ ...prev, [loc]: file }));
        setRawOriginalURLs(prev => { const n = {...prev}; delete n[loc]; return n; });
        setSavedTransforms(prev  => { const n = {...prev}; delete n[loc]; return n; });
        setUploadModalOpen(false);
    };

    const handleOpenDesignEditor = (side, loc = '') => {
        const sl = side==='front'?selectedFrontOptions:side==='back'?selectedBackOptions:side==='left'?['Left Sleeve']:['Right Sleeve'];
        navigate('/design-editor', { state: {
            side, sideLabel: SIDE_LABELS[side],
            imprintLocation: loc || sl[0] || '',
            allSideLocations: sl,
            productConfig: buildProductConfig(),
            existingDesigns: designs,
            rawOriginalURLs,
            // Keep backward-compat with editor fallback chain.
            originalURLs: rawOriginalURLs,
            savedTransforms,
            editorSettings,
            currentStep,
        }});
    };

    const buildProductConfig = () => ({
        product: productData, selectedType, selectedColor, selectedShape,
        selectedFrontOptions, selectedBackOptions, selectedSleeves, quantities, note,
    });

    const handleDesignEmailSent = useCallback((payload) => {
        setDesignEmailMeta(payload);
        try {
            sessionStorage.setItem(designEmailStorageKey, JSON.stringify(payload));
        } catch {}
    }, [designEmailStorageKey]);

    const validate = () => {
        if (!selectedType)                              return 'Please select a type.';
        if (colorOptions.length > 0 && !selectedColor) return 'Please select a color.';
        if (isColorOutOfStock)                         return 'The selected color is out of stock.';
        if (!selectedShape)                            return 'Please select a design shape.';
        if (allActiveLocations.length === 0)           return 'Please select at least one print location.';
        for (const loc of allActiveLocations) { if (!designs[loc]) return `Please upload a design for "${loc}".`; }
        if (totalQuantity === 0)                       return 'Please enter a quantity for at least one size.';
        if (isOverOrderLimit)                          return `Maximum order quantity is ${maxOrderQty} items.`;
        return null;
    };

    const handleAddToCart = async () => {
        if (isPreview) return;
        setValidationError('');
        const err = validate();
        if (err) { setValidationError(err); return; }
        if (!currentUser) { setShowAuthModal(true); return; }
        setAddingToCart(true);
        try {
            const { urls: designUrls, error: uploadError } = await uploadDesignImagesToFirebase(designs);
            if (uploadError) { setValidationError(uploadError); setAddingToCart(false); return; }

            // sizePricesForDisplay is already correctly resolved at render time.
            const effectiveSizePrices = sizePricesForDisplay;

            // Use selectedColorSurcharge already computed at render time from
            // colorPrices — this is always correct and avoids the race condition
            // where prod is null during the async tshirtProducts load.
            const resolvedColorSurcharge = selectedColorSurcharge;

            const cartItem = {
                name:          selectedType,
                image:         productImages[mainImage],
                type:          selectedType,
                color:         selectedColor,
                designShape:   selectedShape === 'circle' ? 'Circle' : 'Rectangle',
                printLocation: allActiveLocations.join(', '),
                quantities:    { ...quantities },
                quantity:      totalQuantity,
                price:         productData.price || 85,
                note,
                subcategory:   SUBCATEGORY_TSHIRT,
                sizeBreakdown: Object.entries(quantities)
                    .filter(([, q]) => q > 0)
                    .map(([s, q]) => `${SIZE_LABELS[s]}: ${q}`)
                    .join(', ') || 'None',
                designImages:    designUrls,
                colorSurcharge:  resolvedColorSurcharge,
                sizePrices:      effectiveSizePrices,
                designEmailSent: Boolean(designEmailMeta?.designEmailSent),
                designEmailSentAt: designEmailMeta?.designEmailSentAt || null,
                storeEmail:      designEmailMeta?.storeEmail || '',
            };

            const { error: cartErr, itemId } = await addCartItem(currentUser.uid, cartItem);
            if (cartErr) { setValidationError('Failed to add item to cart. Please try again.'); return; }
            if (itemId) setLastCartItemId(itemId);
            try {
                sessionStorage.removeItem(designEmailStorageKey);
            } catch {}
            setDesignEmailMeta(null);
            navigate('/cart');
        } catch (e) {
            console.error(e);
            setValidationError('An unexpected error occurred.');
        } finally {
            setAddingToCart(false);
        }
    };

    const UploadSlot = ({ imprintLocation, side }) => {
        const design  = designs[imprintLocation];
        const preview = design ? (typeof design==='string'?design:(design instanceof File?URL.createObjectURL(design):null)) : null;
        const shortName = imprintLocation.length>18 ? imprintLocation.substring(0,18)+'…' : imprintLocation;
        return (
            <div className="upload-slot">
                {design ? (
                    <div className="upload-slot-filled">
                        {preview && <img src={preview} alt={imprintLocation} className="upload-slot-thumb"/>}
                        <div className="upload-slot-info">
                            <span className="upload-location-label">{shortName}</span>
                            <span className="upload-filename">{typeof design==='string'?'Saved':design.name.length>16?design.name.substring(0,16)+'…':design.name}</span>
                        </div>
                        <div className="upload-slot-actions">
                            <button className="upload-edit-btn" onClick={()=>handleOpenDesignEditor(side,imprintLocation)}>✎</button>
                            <button className="upload-remove-btn" onClick={()=>{
                                setDesigns(prev=>{const n={...prev};delete n[imprintLocation];return n;});
                                setRawOriginalURLs(prev=>{const n={...prev};delete n[imprintLocation];return n;});
                                setSavedTransforms(prev=>{const n={...prev};delete n[imprintLocation];return n;});
                            }}>✕</button>
                        </div>
                    </div>
                ) : (
                    <button className="upload-btn" onClick={()=>handleOpenUpload(side,imprintLocation)}>↑ {shortName}</button>
                )}
            </div>
        );
    };

    const UploadGroup = ({ label, locations, side }) => {
        if (locations.length === 0) return null;
        return (
            <div className="upload-group">
                <div className="upload-group-label">{label}</div>
                <div className="upload-group-slots">{locations.map(loc=><UploadSlot key={loc} imprintLocation={loc} side={side}/>)}</div>
            </div>
        );
    };

    return (
        <div className="product-details-container">
            {!isPreview && showAuthModal && <AuthModal isOpen={showAuthModal} onClose={()=>setShowAuthModal(false)}/>}
            {!isPreview && showEmailModal && (
                <SendDesignEmailModal
                storeEmail={storeEmail} productName={selectedType || productData.name}
                cartItemId={lastCartItemId}
                onSent={handleDesignEmailSent}
                onClose={()=>setShowEmailModal(false)}
                userName={userName}
                />
            )}
            {!isPreview && (
                <UploadImageModal
                    isOpen={uploadModalOpen} onClose={()=>setUploadModalOpen(false)} onConfirm={handleUploadConfirm}
                    side={uploadingSide} sideLabel={SIDE_LABELS[uploadingSide]} imprintLocation={uploadingLocation}
                    allSideLocations={uploadingSide==='front'?selectedFrontOptions:uploadingSide==='back'?selectedBackOptions:uploadingSide==='left'?['Left Sleeve']:['Right Sleeve']}
                    productConfig={buildProductConfig()} existingDesigns={designs}
                    rawOriginalURLs={rawOriginalURLs} savedTransforms={savedTransforms}
                    editorSettings={editorSettings}
                    currentStep={currentStep}
                />
            )}

            <div className="page-header">
                <h1>{selectedType || productData.name || 'T-Shirt'}</h1>
                <p>Design your own T-Shirt — soft, stylish, and made for you.</p>
            </div>

            <div className="product-content">
                <div className="image-gallery">
                    <div className="main-image-wrapper">
                        {productImages.length > 1 && <button className="main-image-arrow main-image-arrow--left" onClick={goPrev}>&#8249;</button>}
                        <div className="main-image"><img src={productImages[mainImage]||placeholderShirt} alt={productData.name}/></div>
                        {productImages.length > 1 && <button className="main-image-arrow main-image-arrow--right" onClick={goNext}>&#8250;</button>}
                        {productImages.length > 1 && (
                            <div className="main-image-dots">
                                {productImages.map((_,i)=><button key={i} className={`main-image-dot ${mainImage===i?'active':''}`} onClick={()=>setMainImage(i)}/>)}
                            </div>
                        )}
                    </div>
                    {productImages.length > 1 && (
                        <div className="thumbnails">
                            {productImages.map((img,i)=>(
                                <div key={i} className={`thumbnail ${mainImage===i?'active':''}`} onClick={()=>setMainImage(i)}>
                                    <img src={img} alt={`View ${i+1}`}/>
                                </div>
                            ))}
                        </div>
                    )}
                    {!isPreview && (
                        <div className="note-section">
                            <label>Note: <span className="optional">Optional</span></label>
                            <textarea placeholder="Write a note" value={note} onChange={e=>setNote(e.target.value)}/>
                        </div>
                    )}
                    {!isPreview && (
                        <button className="pd-send-email-btn" onClick={()=>setShowEmailModal(true)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                            Send Design via Email
                        </button>
                    )}
                </div>

                <div className="product-config">
                    {/* Step Progress Indicator */}
                    <div className="steps-progress">
                        <div className="steps-progress-bar">
                            <div className="steps-progress-fill" style={{width: `${((currentStep + 1) / steps.length) * 100}%`}}></div>
                        </div>
                        <div className="steps-breadcrumb">
                            {steps.map((step, idx) => (
                                <button
                                    key={step.id}
                                    className={`step-breadcrumb-item ${idx === currentStep ? 'active' : ''} ${idx < currentStep ? 'completed' : ''}`}
                                    onClick={() => idx < currentStep && setCurrentStep(idx)}
                                    disabled={idx > currentStep}
                                >
                                    <span className="step-number">{idx + 1}</span>
                                    <span className="step-title">{step.title}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Step Content */}
                    <div className="steps-content">
                        {/* Step 1: Type */}
                        {currentStep === 0 && (
                            <div className="step-panel">
                                <h3 className="step-title">Select Type</h3>
                                <div className="config-group">
                                    <select value={selectedType} onChange={e=>{if(!isPreview){setSelectedType(e.target.value);setValidationError('');}}} disabled={isPreview}>
                                        <option value="" disabled hidden>Select type</option>
                                        {tshirtProducts.map(p=>(
                                            <option key={p.id} value={p.name} disabled={p.status==='inactive'}>
                                                {p.name}{p.status==='inactive'?' (Unavailable)':''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {STEP_TIPS[currentStep] && (
                                    <div className="pd-tip-card">
                                        <p className="pd-tip-card-title">{STEP_TIPS[currentStep].title}</p>
                                        <div className="pd-tip-card-text">{STEP_TIPS[currentStep].content}</div>
                                    </div>
                                )}
                                <div className="step-actions">
                                    <button className="step-btn step-btn--next" onClick={() => selectedType && setCurrentStep(1)} disabled={!selectedType || isPreview}>
                                        Next →
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 2: Color */}
                        {currentStep === 1 && (
                            <div className="step-panel">
                                <h3 className="step-title">Choose Color</h3>
                                <div className="config-group">
                                    {!selectedType ? (
                                        <p style={{fontSize:'13px',color:'#999',margin:0}}>Select a type first.</p>
                                    ) : colorOptions.length === 0 ? (
                                        <div className="color-options">
                                            <button className="color-btn border selected" style={{backgroundColor:'#FFFFFF'}} disabled/>
                                            <span style={{fontSize:'13px',color:'#666',marginLeft:'8px'}}>White only</span>
                                        </div>
                                    ) : (
                                        <div>
                                            <div className="color-options">
                                                {colorOptions.map(({rawName,key,hex})=>{
                                                    const cs  = getColorTotalStock(productInventory,rawName)??getColorTotalStock(productInventory,key);
                                                    const oos = cs !== null && cs === 0;
                                                    const surcharge = colorPrices[key]||colorPrices[rawName]||0;
                                                    return (
                                                        <div key={key} className="pd-color-option-wrap">
                                                            <button
                                                                className={`color-btn ${selectedColor===key?'selected':''} ${oos?'pd-color-btn--oos':''}`}
                                                                style={{backgroundColor:hex,border:key==='white'?'2px solid #ddd':'none'}}
                                                                onClick={()=>!oos&&!isPreview&&setSelectedColor(key)}
                                                                title={oos?`${rawName} — Out of Stock`:`${rawName}${surcharge>0?` (+₱${surcharge})`:''}`}
                                                                disabled={oos||isPreview}
                                                            />
                                                            {oos  && <span className="pd-color-oos-label">OOS</span>}
                                                            {!oos && surcharge>0 && <span className="pd-color-surcharge-label">+₱{surcharge}</span>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {isColorOutOfStock&&<div className="pd-stock-warning pd-stock-warning--out">⚠️ This color is out of stock.</div>}
                                            {!isColorOutOfStock&&colorTotalStock!==null&&colorTotalStock<=LOW_STOCK_THRESHOLD&&colorTotalStock>0&&(
                                                <div className="pd-stock-warning pd-stock-warning--low">⚠️ Only {colorTotalStock} units left.</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {STEP_TIPS[currentStep] && (
                                    <div className="pd-tip-card">
                                        <p className="pd-tip-card-title">{STEP_TIPS[currentStep].title}</p>
                                        <div className="pd-tip-card-text">{STEP_TIPS[currentStep].content}</div>
                                    </div>
                                )}
                                <div className="step-actions">
                                    <button className="step-btn step-btn--back" onClick={() => setCurrentStep(0)}>← Back</button>
                                    <button className="step-btn step-btn--next" onClick={() => selectedColor && setCurrentStep(2)} disabled={!selectedColor || isPreview}>
                                        Next →
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Design Shape */}
                        {currentStep === 2 && (
                            <div className="step-panel">
                                <h3 className="step-title">Design Shape</h3>
                                <div className="config-group">
                                    <p className="step-info-text"><em>Choose the shape of your printed design</em></p>
                                    <div className="button-group">
                                        <button className={selectedShape==='circle'?'active':''} onClick={()=>!isPreview&&setSelectedShape('circle')} disabled={isPreview}>Circle</button>
                                        <button className={selectedShape==='rectangle'?'active':''} onClick={()=>!isPreview&&setSelectedShape('rectangle')} disabled={isPreview}>Rectangle</button>
                                    </div>
                                </div>
                                {STEP_TIPS[currentStep] && (
                                    <div className="pd-tip-card">
                                        <p className="pd-tip-card-title">{STEP_TIPS[currentStep].title}</p>
                                        <div className="pd-tip-card-text">{STEP_TIPS[currentStep].content}</div>
                                    </div>
                                )}
                                <div className="step-actions">
                                    <button className="step-btn step-btn--back" onClick={() => setCurrentStep(1)}>← Back</button>
                                    <button className="step-btn step-btn--next" onClick={() => setCurrentStep(3)} disabled={!selectedShape || isPreview}>
                                        Next →
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 4: Print Location */}
                        {currentStep === 3 && (
                            <div className="step-panel">
                                <h3 className="step-title">Print Location</h3>
                                <div className="config-group">
                                    <p className="step-info-text"><em>Maximum selections Front: 3 · Back: 2 · 1 per sleeve</em></p>
                                    <div className="print-location-all">
                                        <div className="print-location-popup-wrapper" ref={frontRef}>
                                            <button className={`button-group-btn ${frontDropdownOpen||selectedFrontOptions.length>0?'active':''}`}
                                                onClick={()=>{if(isPreview)return;setFrontDropdownOpen(o=>!o);setBackDropdownOpen(false);}} disabled={isPreview}>
                                                Front {selectedFrontOptions.length>0?`(${selectedFrontOptions.length}/3)`:''}
                                            </button>
                                            {frontDropdownOpen&&!isPreview&&(
                                                <div className="print-location-dropdown">
                                                    <div className="pd-back-single-note">Select up to {MAX_FRONT_LOCATIONS} front locations</div>
                                                    {frontLimitWarning&&<div className="pd-location-limit-warning">⚠ Max {MAX_FRONT_LOCATIONS} front locations</div>}
                                                    <div className="dropdown-options-grid two-col">
                                                        {FRONT_OPTIONS.map(opt=>{
                                                            const sel=selectedFrontOptions.includes(opt);
                                                            const blk=blockedFront.has(opt)&&!sel;
                                                            const atLimit=!sel&&selectedFrontOptions.length>=MAX_FRONT_LOCATIONS;
                                                            return(
                                                                <button key={opt} className={`print-location-option ${sel?'selected':''} ${blk||atLimit?'blocked':''}`}
                                                                    onClick={()=>!blk&&toggleFrontOption(opt)} disabled={blk||atLimit}>
                                                                    {opt}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                    {selectedFrontOptions.length>0&&(
                                                        <button className="print-location-option clear-option" onClick={()=>setSelectedFrontOptions([])}>✕ Clear all ({selectedFrontOptions.length})</button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="print-location-popup-wrapper" ref={backRef}>
                                            <button className={`button-group-btn ${backDropdownOpen||selectedBackOptions.length>0?'active':''}`}
                                                onClick={()=>{if(isPreview)return;setBackDropdownOpen(o=>!o);setFrontDropdownOpen(false);}} disabled={isPreview}>
                                                Back {selectedBackOptions.length>0?`(${selectedBackOptions.length}/2)`:''}
                                            </button>
                                            {backDropdownOpen&&!isPreview&&(
                                                <div className="print-location-dropdown">
                                                    <div className="pd-back-single-note">Select up to {MAX_BACK_LOCATIONS} back locations</div>
                                                    <div className="dropdown-options-grid">
                                                        {backOptions.map(opt=>{
                                                            const sel=selectedBackOptions.includes(opt);
                                                            const blk=blockedBack.has(opt)&&!sel;
                                                            const atLimit=!sel&&selectedBackOptions.length>=MAX_BACK_LOCATIONS;
                                                            return(
                                                                <button key={opt} className={`print-location-option ${sel?'selected':''} ${blk||atLimit?'blocked':''}`}
                                                                    onClick={()=>!blk&&toggleBackOption(opt)} disabled={blk||atLimit}>
                                                                    {opt}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                    {selectedBackOptions.length>0&&(
                                                        <button className="print-location-option clear-option" onClick={()=>setSelectedBackOptions([])}>✕ Clear all ({selectedBackOptions.length})</button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <button className={selectedSleeves.includes('left')?'active':''} onClick={()=>!isPreview&&setSelectedSleeves(prev=>prev.includes('left')?prev.filter(s=>s!=='left'):[...prev,'left'])} disabled={isPreview}>Left Sleeve</button>
                                        <button className={selectedSleeves.includes('right')?'active':''} onClick={()=>!isPreview&&setSelectedSleeves(prev=>prev.includes('right')?prev.filter(s=>s!=='right'):[...prev,'right'])} disabled={isPreview}>Right Sleeve</button>
                                    </div>
                                </div>
                                {STEP_TIPS[currentStep] && (
                                    <div className="pd-tip-card">
                                        <p className="pd-tip-card-title">{STEP_TIPS[currentStep].title}</p>
                                        <div className="pd-tip-card-text">{STEP_TIPS[currentStep].content}</div>
                                    </div>
                                )}
                                <div className="step-actions">
                                    <button className="step-btn step-btn--back" onClick={() => setCurrentStep(2)}>← Back</button>
                                    <button className="step-btn step-btn--next" onClick={() => allActiveLocations.length > 0 && setCurrentStep(4)} disabled={allActiveLocations.length === 0 || isPreview}>
                                        Next →
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 5: Upload Design */}
                        {currentStep === 4 && allActiveLocations.length > 0 && !isPreview && (
                            <div className="step-panel">
                                <h3 className="step-title">Upload Design</h3>
                                <div className="config-group">
                                    <div className="upload-groups-container">
                                        {selectedFrontOptions.length>0&&<UploadGroup label="Front" locations={selectedFrontOptions} side="front"/>}
                                        {selectedBackOptions.length>0&&<UploadGroup label="Back" locations={selectedBackOptions} side="back"/>}
                                        {selectedSleeves.includes('left')&&<UploadGroup label="Left Sleeve" locations={['Left Sleeve']} side="left"/>}
                                        {selectedSleeves.includes('right')&&<UploadGroup label="Right Sleeve" locations={['Right Sleeve']} side="right"/>}
                                    </div>
                                </div>
                                {STEP_TIPS[currentStep] && (
                                    <div className="pd-tip-card">
                                        <p className="pd-tip-card-title">{STEP_TIPS[currentStep].title}</p>
                                        <div className="pd-tip-card-text">{STEP_TIPS[currentStep].content}</div>
                                    </div>
                                )}
                                <div className="step-actions">
                                    <button className="step-btn step-btn--back" onClick={() => setCurrentStep(3)}>← Back</button>
                                    <button className="step-btn step-btn--next" onClick={() => allActiveLocations.every(loc => designs[loc]) && setCurrentStep(5)} disabled={!allActiveLocations.every(loc => designs[loc]) || isPreview}>
                                        Next →
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 6: Quantity */}
                        {currentStep === 5 && (
                            <div className="step-panel">
                                <h3 className="step-title">Quantity</h3>
                                <div className="config-group">
                                    <div className="pd-order-limit-bar">
                                        <span className={`pd-order-limit-text ${isOverOrderLimit?'pd-order-limit-text--over':totalQuantity>maxOrderQty*0.8?'pd-order-limit-text--warn':''}`}>
                                            {totalQuantity} / {maxOrderQty} items
                                        </span>
                                        {isOverOrderLimit&&<span className="pd-order-limit-badge">Over limit</span>}
                                    </div>
                                    <div className="quantity-grid">
                                        {ALL_SIZES.filter(s=>availableSizes.includes(s)).map(size=>{
                                            const sp  = selectedType?(sizePricesForDisplay[size]??null):null;
                                            const displayPrice = sp!==null?(sp+selectedColorSurcharge):null;
                                            const st  = getSizeStockForUI(size);
                                            const oos = st!==null&&st===0;
                                            const low = st!==null&&st>0&&st<=LOW_STOCK_THRESHOLD;
                                            return(
                                                <div key={size} className={`quantity-item${oos?' quantity-item--oos':low?' quantity-item--low':''}`}>
                                                    <label>
                                                        {SIZE_LABELS[size]}
                                                        {displayPrice!==null&&!oos&&<span className="size-price-tag">₱{displayPrice}</span>}
                                                        {oos&&<span className="size-oos-tag">Out of stock</span>}
                                                        {low&&!oos&&<span className="size-low-tag">Only {st} left</span>}
                                                    </label>
                                                    <div className="quantity-stepper">
                                                        <button className="quantity-stepper-btn" disabled={isPreview || oos || (quantities[size] || 0) <= 0}
                                                            onClick={() => handleQuantityChange(size, (quantities[size] || 0) - 1)}>−</button>
                                                        <input type="number" min="0" max={st!==null&&st>0?st:maxOrderQty}
                                                            value={oos?'':(quantities[size]===0?'':quantities[size])}
                                                            placeholder={oos?'—':'0'} disabled={oos||isPreview}
                                                            onChange={e=>{if(isPreview)return;const v=e.target.value;if(v===''){handleQuantityChange(size,0);return;}const n=parseInt(v);if(!isNaN(n)&&n>=0)handleQuantityChange(size,n);}}
                                                            className={`quantity-input${oos?' quantity-input--oos':low?' quantity-input--low':isOverOrderLimit?' quantity-input--over':''}`}
                                                        />
                                                        <button className="quantity-stepper-btn" disabled={isPreview || oos || totalQuantity >= maxOrderQty || (st !== null && st > 0 && (quantities[size] || 0) >= st)}
                                                            onClick={() => handleQuantityChange(size, (quantities[size] || 0) + 1)}>+</button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Total */}
                                <div className="total-section">
                                    {selectedType&&(
                                        <div className="total-breakdown">
                                            <div className="total-breakdown-row"><span>Shirt subtotal:</span><span>₱ {shirtSubtotal.toLocaleString()}</span></div>
                                            {selectedColorSurcharge>0&&totalQuantity>0&&(
                                                <div className="total-breakdown-row total-breakdown-row--color">
                                                    <span>Color surcharge ({selectedColor}, ₱{selectedColorSurcharge}/pc):</span><span>included above</span>
                                                </div>
                                            )}
                                            {imprintSubtotal>0&&<div className="total-breakdown-row"><span>Print subtotal:</span><span>₱ {imprintSubtotal.toLocaleString()}</span></div>}
                                            <div className="total-divider"/>
                                        </div>
                                    )}
                                    <span className="total-label">Total:</span>
                                    <span className="total-amount">₱ {totalPrice.toLocaleString()}</span>
                                </div>

                                {!isPreview&&isOverOrderLimit&&<div className="pd-order-limit-error">⚠ Maximum order is {maxOrderQty} items.</div>}
                                {!isPreview&&validationError&&<div className="error-message">{validationError}</div>}

                                <div className="step-actions">
                                    <button className="step-btn step-btn--back" onClick={() => setCurrentStep(4)}>← Back</button>
                                    <button className="add-to-cart-btn" onClick={handleAddToCart}
                                        disabled={addingToCart||isColorOutOfStock||isPreview||isOverOrderLimit||totalQuantity===0}
                                        style={isPreview?{backgroundColor:'#aaa',cursor:'not-allowed'}:{}}>
                                        {isPreview?'Not Available in Preview Mode':addingToCart?'Uploading & Adding...':'Add to Cart'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {activeDescription && (
                <ProductDescriptionBlock 
                    description={activeDescription}
                    title={`About ${activeDescriptionTitle}`}
                    highlightColor="#111"
                />
            )}

            {/* Product Information Tabs */}
            <div className="product-information" id="size-chart">
                <h2>Product Information</h2>
                <div className="info-content">
                    <div className="info-tabs">
                        {[{key:'details',label:'Product Details',icon:'📦'},{key:'guidelines',label:'Print Guidelines',icon:'📋'},{key:'size',label:'Size Chart',icon:'📏'},{key:'setup',label:'File Setup',icon:'📄'},{key:'template',label:'Template',icon:'🎨'}].map(({key,label,icon})=>(
                            <button key={key} className={`tab-btn ${activeTab===key?'active':''}`} onClick={()=>setActiveTab(key)}>
                                <span className="tab-icon">{icon}</span> {label}
                            </button>
                        ))}
                    </div>
                    <div className="info-panel">
                        <div className="tab-content">
                            {activeTab==='details'&&(<div className="tab-details"><div className="tab-details-grid"><div className="tab-details-col"><div className="tab-detail-row"><span className="tab-detail-label">Type:</span><span className="tab-detail-value">Crew Neck<br/>V-Neck</span></div><div className="tab-detail-row"><span className="tab-detail-label">Material:</span><span className="tab-detail-value">Ultra Soft · Stretchable · Side Seamed · Pre-shrunk · Machine Washable</span></div><div className="tab-detail-row"><span className="tab-detail-label">Colors:</span><span className="tab-detail-value">White · Black · Red · Blue · Navy · Athletic Heather · Cream/Natural</span></div></div><div className="tab-details-col"><div className="tab-detail-row"><span className="tab-detail-label">Sizes:</span><span className="tab-detail-value">Men/Unisex — Small to 3X-Large<br/><br/>Women — X-Small to 2X-Large</span></div><div className="tab-detail-row"><span className="tab-detail-label">Notes:</span><span className="tab-detail-value">T-shirt brands, colors, and materials may vary.<br/>Wash before wearing.</span></div></div></div></div>)}
                            {activeTab==='guidelines'&&(<div className="tab-guidelines"><div className="guidelines-banner"><div className="guidelines-banner-icon"></div><span className="guidelines-banner-title">Crew Neck</span></div><p className="guidelines-intro">Front design placement is determined by the number of inches from the top of the front neckline.</p><div className="guidelines-group"><button className="guidelines-toggle" onClick={()=>setOpenGuidelines(p=>({...p,'Men/Unisex':!p['Men/Unisex']}))}>​<span>{openGuidelines['Men/Unisex']?'▾':'▸'}</span> Men/Unisex</button>{openGuidelines['Men/Unisex']&&(<table className="guidelines-table"><thead><tr><th>T-Shirt Size</th><th>Plate Size</th><th>Front Collar</th><th>Back Collar</th></tr></thead><tbody><tr><td>Small</td><td>12" x 15"</td><td>3"</td><td>3.5"</td></tr><tr><td>Medium</td><td>16" x 20"</td><td>3"</td><td>3.25"</td></tr><tr><td>Large</td><td>16" x 20"</td><td>3"</td><td>3.25"</td></tr><tr><td>X-Large</td><td>16" x 20"</td><td>3"</td><td>3.5"</td></tr><tr><td>2X-Large</td><td>16" x 20"</td><td>3"</td><td>3.5"</td></tr></tbody></table>)}</div><div className="guidelines-group"><button className="guidelines-toggle" onClick={()=>setOpenGuidelines(p=>({...p,'Women':!p['Women']}))}>​<span>{openGuidelines['Women']?'▾':'▸'}</span> Women</button>{openGuidelines['Women']&&(<table className="guidelines-table"><thead><tr><th>T-Shirt Size</th><th>Plate Size</th><th>Front Collar</th><th>Back Collar</th></tr></thead><tbody><tr><td>X-Small</td><td>12" x 15"</td><td>3"</td><td>3.5"</td></tr><tr><td>Small</td><td>12" x 15"</td><td>3"</td><td>3.5"</td></tr><tr><td>Medium</td><td>12" x 15"</td><td>2.5"</td><td>3.5"</td></tr><tr><td>Large</td><td>16" x 20"</td><td>3"</td><td>3.25"</td></tr><tr><td>X-Large</td><td>16" x 20"</td><td>3"</td><td>3.25"</td></tr><tr><td>2X-Large</td><td>16" x 20"</td><td>3"</td><td>3"</td></tr></tbody></table>)}</div></div>)}
                            {activeTab==='size'&&(<div className="tab-size-chart"><div className="size-chart-layout"><div className="size-chart-image-wrap"><img src="/images/size-chart-shirt.png" alt="Size chart" className="size-chart-img" onError={e=>{e.target.style.display='none';}}/><div className="size-chart-img-placeholder"><span>Size Diagram</span></div></div><div className="size-chart-info"><div className="size-chart-measure"><h4 className="size-chart-measure-title" style={{color:'#8bc34a'}}>Chest</h4><p>Measured across the chest one inch below armhole when laid flat</p></div><div className="size-chart-measure"><h4 className="size-chart-measure-title" style={{color:'#29b6f6'}}>Length</h4><p>Measured from high point shoulder to shirt bottom</p></div><div className="size-chart-help"><h4>Need help with sizing?</h4><p>Lay a t-shirt on a flat surface and measure as displayed.</p><ul className="size-chart-notes"><li>* Please allow a +/- 0.5" tolerance.</li><li>* T-Shirt brands may vary.</li><li>* Wash before wearing.</li></ul></div></div></div></div>)}
                            {activeTab==='setup'&&(<div className="tab-file-setup"><div className="file-setup-row"><span className="file-setup-label">Bleed:</span><span className="file-setup-value">Not required. Recommended: 14" x 16" Vertical.</span></div><div className="file-setup-row"><span className="file-setup-label">Resolution:</span><span className="file-setup-value">350 DPI</span></div><div className="file-setup-row"><span className="file-setup-label">Color Mode:</span><span className="file-setup-value" style={{color:'#29b6f6'}}>RGB</span></div><div className="file-setup-row"><span className="file-setup-label">File Formats:</span><span className="file-setup-value">TIF, TIFF, EPS, AI, PSD, BMP, GIF, JPG, PNG, PDF</span></div><div className="file-setup-row"><span className="file-setup-label">Max File Size:</span><span className="file-setup-value">75MB</span></div></div>)}
                            {activeTab==='template'&&(<div className="tab-template"><p className="template-intro">White backgrounds on your design will be printed. Save with transparent background for best results.</p><h4 className="guidelines-section-title">▾ T-Shirt Size Options</h4><table className="guidelines-table"><thead><tr><th>Size</th><th>Front/Back</th></tr></thead><tbody><tr><td>14" x 16" - Vertical</td><td><div className="template-format-icons"><span className="template-icon ai">Ai</span><span className="template-icon ps">Ps</span><span className="template-icon pdf">PDF</span><span className="template-icon id">Id</span><span className="template-icon jpg">JPG</span></div></td></tr></tbody></table></div>)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProductDetails;
